import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import config from "../config/config";

const shareSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  type: z.string().min(1, "Required"),
  category: z.string().min(1, "Required"),
  slaughteringDay: z.string().min(1, "Required"),
  isFisabilillah: z.boolean().default(false),
});

const formSchema = z.object({
  numShares: z.preprocess((val) => Number(val), z.number().min(1)),
  shareDetails: z.array(shareSchema),
});

export function ShareDetailsForm({ registrationData }) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCustomerId, setShowCustomerId] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [animalTypes, setAnimalTypes] = useState([]);
  const [categoriesByShare, setCategoriesByShare] = useState({});
  const [availableDays, setAvailableDays] = useState({});
  const [fullDays, setFullDays] = useState({});

  const { register, handleSubmit, control, setValue, watch } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      numShares: 1,
      shareDetails: [{ firstName: "", lastName: "", type: "", category: "", slaughteringDay: "", isFisabilillah: false }]
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "shareDetails" });
  const watchedDetails = useWatch({ control, name: "shareDetails" });
  const watchedNumShares = watch("numShares");

  // ✅ Summary Logic with safe array checks
  const summaryData = useMemo(() => {
    const summary = {};
    let totalAmount = 0;
    let hasValidItem = false;

    if (Array.isArray(watchedDetails)) {
      watchedDetails.forEach((share, index) => {
        if (share?.type && share?.category) {
          const typeObj = animalTypes.find(t => String(t.id) === String(share.type));
          const catList = categoriesByShare[index] || [];
          const catObj = catList.find(c => String(c.id) === String(share.category));

          if (catObj) {
            hasValidItem = true;
            const key = `${typeObj?.cat_name || 'Animal'} - ${catObj.sub_cat}`;
            if (!summary[key]) {
              summary[key] = { count: 0, price: Number(catObj.price) || 0 };
            }
            summary[key].count += 1;
            totalAmount += Number(catObj.price || 0);
          }
        }
      });
    }
    return { items: Object.entries(summary), totalAmount, showTable: hasValidItem };
  }, [watchedDetails, animalTypes, categoriesByShare]);

  // Handle number of shares changes
  useEffect(() => {
    const newVal = parseInt(watchedNumShares) || 0;
    const currentVal = fields.length;
    if (newVal > currentVal) {
      for (let i = 0; i < newVal - currentVal; i++) {
        append({ firstName: "", lastName: "", type: "", category: "", slaughteringDay: "", isFisabilillah: false });
      }
    } else if (newVal < currentVal && newVal > 0) {
      for (let i = 0; i < currentVal - newVal; i++) {
        remove(currentVal - 1 - i);
      }
    }
  }, [watchedNumShares, fields.length, append, remove]);

  // Initial fetch for types
  useEffect(() => {
    const fetchAnimalTypes = async () => {
      try {
        const response = await fetch(`${config.baseUrl}/api/subcategories/filteranimal-type`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await response.json();
        setAnimalTypes(Array.isArray(data) ? data : []);
      } catch (e) { console.error(e); }
    };
    fetchAnimalTypes();
  }, []);

  const handleTypeChange = async (e, index) => {
    const val = e.target.value;
    setValue(`shareDetails.${index}.type`, val);
    setValue(`shareDetails.${index}.category`, "");
    setValue(`shareDetails.${index}.slaughteringDay`, "");

    // Resetting dependent states with empty arrays to prevent .map errors
    setCategoriesByShare(prev => ({ ...prev, [index]: [] }));
    setAvailableDays(prev => ({ ...prev, [index]: [] }));
    setFullDays(prev => ({ ...prev, [index]: [] }));

    if (!val) return;
    try {
      const res = await fetch(`${config.baseUrl}/api/subcategories/filtersubcat/${val}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      setCategoriesByShare(prev => ({ ...prev, [index]: Array.isArray(data) ? data : [] }));
    } catch (err) { console.error(err); }
  };

  const handleCategoryChange = (e, index) => {
    const catId = e.target.value;
    setValue(`shareDetails.${index}.category`, catId);
    setValue(`shareDetails.${index}.slaughteringDay`, "");

    if (!catId) return;
    const cat = (categoriesByShare[index] || []).find(c => String(c.id) === String(catId));
    const available = [];
    const soldOut = [];

    if (cat) {
      if (Number(cat.day1_limit) > 0) available.push("1"); else soldOut.push("1");
      if (Number(cat.day2_limit) > 0) available.push("2"); else soldOut.push("2");
      if (Number(cat.day3_limit) > 0) available.push("3"); else soldOut.push("3");
    }
    setAvailableDays(prev => ({ ...prev, [index]: available }));
    setFullDays(prev => ({ ...prev, [index]: soldOut }));
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const genId = `QRB${Math.floor(Math.random() * 90000) + 10000}`;

      // Har share ke liye price fetch karna
      const shareDataWithPrice = data.shareDetails.map((s, index) => {
        const catList = categoriesByShare[index] || [];
        const selectedCat = catList.find(c => String(c.id) === String(s.category));

        return {
          ...s,
          customer_id: genId,
          firstname: s.firstName,
          lastname: s.lastName,
          slaughtering_day: s.slaughteringDay,
          feasabilillah: s.isFisabilillah,
          // Yahan price add ho rahi hai
          price: Number(selectedCat?.price)
        };
      });

      await fetch(`${config.baseUrl}/api/qurbani/share-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          shareDetails: shareDataWithPrice, // Updated array with prices
          registrationId: registrationData.id,
          totalAmount: summaryData.totalAmount // Yeh total amount hai
        })
      });
      console.log(shareDataWithPrice);

      setCustomerId(genId);
      setShowCustomerId(true);
    } catch (e) {
      console.error(e);
      alert("Error saving data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none border-gray-200 transition-all";

  if (showCustomerId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full p-8 bg-white rounded-[2.5rem] shadow-2xl text-center border border-white">
          <h2 className="text-2xl font-black text-slate-900 mb-6 uppercase tracking-tight">Booking Successful!</h2>
          <div className="bg-slate-50 border-2 border-dashed border-blue-100 rounded-3xl p-6 mb-6">
            <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest block mb-1">Customer ID</span>
            <h3 className="text-4xl font-mono font-black text-slate-800">{customerId}</h3>
          </div>
          <p className="text-xl font-black text-slate-900 mb-6">Total Amount: Rs. {summaryData.totalAmount.toLocaleString()}</p>
          <div className="bg-blue-600 text-white p-5 rounded-2xl mb-8">
            <p className="text-lg font-bold" dir="rtl">برائے مہربانی کسٹمر آئی ڈی کو Save کر لیں۔ payment کے وقت دکھانا لازمی ہوگا۔</p>
          </div>
          <button onClick={() => navigate('/')} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest">Finish</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-2 ">
      <div className="max-w-4xl mx-auto space-y-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <h1 className="text-2xl text-center font-black text-slate-800 uppercase mb-4">Share Holder Details</h1>

          <div className="bg-white p-5 flex flex-col md:flex-row items-center justify-between gap-4">
            <h1 className="text-sm font-black text-slate-800 uppercase text-xl">Total Number of Shares</h1>
            <input type="number" {...register("numShares")} className="w-20 px-1 py-3 border rounded-lg text-center text-xl font-black text-indigo-900" min="1" />
          </div>

          {fields.map((field, index) => (
            <div key={field.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-blue-600"></div>

              {/* Section Heading */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Share Holder #{index + 1} Details</h3>
                <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Required</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Name Section */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Share Holder Name</label>
                  <input {...register(`shareDetails.${index}.firstName`)} className={inputCls} placeholder="First Name" />
                </div>

                {/* Father Name Section */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Father/Husband Name</label>
                  <div className="flex items-center gap-3">
                    <input {...register(`shareDetails.${index}.lastName`)} className={inputCls} placeholder="Name" />
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <input
                        type="checkbox"
                        id={`fisabilillah-${index}`}
                        {...register(`shareDetails.${index}.isFisabilillah`)}
                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                      />
                      <label htmlFor={`fisabilillah-${index}`} className="text-[10px] font-black text-slate-600 cursor-pointer uppercase">Fisabilillah</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection Section */}
              <div className="pt-2">
                <div className="grid grid-cols-3 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Animal Type </label>

                    <select {...register(`shareDetails.${index}.type`)} onChange={(e) => handleTypeChange(e, index)} className={inputCls}>
                      <option value="">Select Animal</option>
                      {(animalTypes || []).map(t => <option key={t.id} value={t.id}>{t.cat_name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Animal Category</label>

                    <select {...register(`shareDetails.${index}.category`)} onChange={(e) => handleCategoryChange(e, index)} className={inputCls} disabled={!watchedDetails?.[index]?.type}>
                      <option value="">Select Category</option>
                      {(categoriesByShare[index] || []).map(cat => <option key={cat.id} value={cat.id}>{cat.sub_cat}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">slaughtering Day</label>
                    <select {...register(`shareDetails.${index}.slaughteringDay`)} className={inputCls} disabled={!watchedDetails?.[index]?.category}>
                      <option value="">Select Day</option>
                      {(availableDays[index] || []).map(day => <option key={day} value={day}>Day {day}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Alert Section */}
              {Array.isArray(fullDays[index]) && fullDays[index].length > 0 && (
                <div className="relative overflow-hidden p-3 bg-red-600 rounded-2xl flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-white animate-ping"></div>
                  <p className="text-[11px] font-black text-white uppercase tracking-wider">
                    {fullDays[index].map(d => `Day ${d}`).join(" & ")} is fully booked.
                  </p>
                </div>
              )}
            </div>
          ))}

          {summaryData.showTable && (
            <div className="bg-slate-900 overflow-x-auto rounded-3xl shadow-xl p-6 text-white mt-8 border border-slate-800">
              <h3 className="text-[10px] font-black uppercase text-blue-400 tracking-[0.2em] mb-6">Order Summary</h3>
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-500 font-black uppercase">
                    <th className="pb-3">Item</th>
                    <th className="pb-3 text-center">Qty</th>
                    <th className="pb-3 text-right">Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {summaryData.items.map(([label, data], i) => (
                    <tr key={i}>
                      <td className="py-2 text-slate-300 font-bold">{label}</td>
                      <td className="py-2 text-center text-blue-400 font-black">{data.count}</td>
                      <td className="py-2 text-right font-mono text-white">Rs. {data.price.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-6 flex justify-between border-t border-slate-800 pt-6 items-center">
                <span className="font-black text-slate-500 uppercase text-[10px]">Total Payable</span>
                <span className="text-xl font-black text-blue-400">Rs. {summaryData.totalAmount.toLocaleString()}</span>
              </div>
            </div>
          )}

          <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-indigo-800 text-white font-black rounded-md uppercase tracking-widest mt-6 shadow-xl active:scale-95 disabled:opacity-50">
            {isSubmitting ? "Processing..." : "Complete Booking"}
          </button>
        </form>
      </div>
    </div>
  );
}